import { useMemo } from "react";
import { useSelector } from "react-redux";
import type {
  DateRange,
  DiscountAwarePrices,
  Lodging,
} from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useGetPriceDisplay } from "@tenant/ui";
import dayjs from "dayjs";
import { AccountSelectors } from "@/modules/account";
import { getPercentOff } from "@/utilities/hotels/discount";

const getStrikethroughPrice = (
  originalPrice: string | null,
  currentPrice: string
) => (originalPrice && originalPrice !== currentPrice ? originalPrice : null);

export const getHasDiscount = (totalDiscountAware: DiscountAwarePrices) => {
  const discounted =
    totalDiscountAware.priceWithAllDiscounts ??
    totalDiscountAware.priceWithUnmanagedDiscounts;
  return (
    discounted?.fiat?.value !==
    totalDiscountAware.priceWithNoDiscounts?.fiat?.value
  );
};

export const getTotalNights = (dateRange?: DateRange) => {
  if (dateRange == null) {
    return 1;
  }
  const fromDate = dayjs(dateRange.from);
  const untilDate = dayjs(dateRange.until);
  return untilDate.diff(fromDate, "days");
};

export const useAvailabilityHotelPrice = (
  hotel: Lodging,
  totalNightsCount?: number,
  rooms = 1,
  dateRange?: DateRange
) => {
  const { t } = useI18nContext();
  const { getPriceDisplay } = useGetPriceDisplay();

  const rewardsAccountId = useSelector(AccountSelectors.getRewardsAccountId);

  const totalNights = totalNightsCount ?? getTotalNights(dateRange);

  return useMemo(() => {
    const { price } = hotel;

    if (price == null) {
      return;
    }

    const accountSpecificPrice =
      price.breakdown.total.accountSpecific?.[rewardsAccountId];
    const showPointsPricing =
      rewardsAccountId != null &&
      accountSpecificPrice != null &&
      Object.keys(accountSpecificPrice).length > 0;

    // Nightly price — prefer priceWithAllDiscounts (hotel-funded + DMO combined),
    // fall back to priceWithUnmanagedDiscounts if missing at runtime.
    // Object-level ?? is intentional: the API returns complete Prices objects
    // (fiat + accountSpecific) or omits the field; partial objects don't occur.
    const nightlyDA = price.nightlyDiscountAwareTaxesAndFeesIncluded;
    const nightlyDiscounted =
      nightlyDA.priceWithAllDiscounts ?? nightlyDA.priceWithUnmanagedDiscounts;
    const nightlyPriceWithNoDiscounts = nightlyDA.priceWithNoDiscounts;
    const hasDiscount =
      nightlyDiscounted.fiat?.value !== nightlyPriceWithNoDiscounts.fiat?.value;
    const { priceText: nightlyTotalPrice, rewardsText: nightlyPoints } =
      getPriceDisplay({
        prices: hasDiscount ? nightlyDiscounted : nightlyPriceWithNoDiscounts,
      });

    const nightlyNotDiscountedPrice = hasDiscount
      ? getPriceDisplay({ prices: nightlyPriceWithNoDiscounts }).priceText
      : null;

    const nightlyPriceDifference = {
      ...nightlyDiscounted.fiat,
      value: Math.floor(
        nightlyPriceWithNoDiscounts.fiat.value - nightlyDiscounted.fiat.value
      ),
    };

    const nightlyPriceDifferenceFormatted = {
      total: getPriceDisplay({
        prices: { fiat: nightlyPriceDifference, accountSpecific: {} },
      }).priceText,
      percentage: Math.floor(
        (nightlyPriceDifference.value /
          nightlyPriceWithNoDiscounts.fiat.value) *
          100
      ),
    };

    // Total price — same strategy: priceWithAllDiscounts with fallback.
    const totalDA = price.totalDiscountAwareTaxesAndFeesIncluded;
    const totalDiscounted =
      totalDA.priceWithAllDiscounts ?? totalDA.priceWithUnmanagedDiscounts;
    const totalPriceWithNoDiscounts = totalDA.priceWithNoDiscounts;
    const totalHasDiscount =
      totalDiscounted.fiat?.value !== totalPriceWithNoDiscounts.fiat?.value;

    const percentOff = getPercentOff(
      price.totalDiscountAwareTaxesAndFeesIncluded
    );

    const { priceText: totalPrice, rewardsText: totalPoints } = getPriceDisplay(
      {
        prices: totalHasDiscount ? totalDiscounted : totalPriceWithNoDiscounts,
      }
    );

    const {
      priceText: totalNotDiscountedPrice,
      rewardsText: totalNotDiscountedPoints,
    } = totalHasDiscount
      ? getPriceDisplay({ prices: totalPriceWithNoDiscounts })
      : { priceText: null, rewardsText: null };

    return {
      context: {
        totalPrice,
        nightlyTotalPrice,
        nightlyNotDiscountedPrice,
        nightlyPriceDifferenceFormatted,
        nightlyPoints,
        totalPoints,
        showPointsPricing,
        totalNights,
        percentOff,
      },
      text: {
        pricePerNight: {
          final: showPointsPricing
            ? t(
                "core-hotels.availabilityPage.results.hotelCard.price.pointsPerNight",
                {
                  points: nightlyPoints,
                  count: rooms,
                }
              )
            : t(
                "core-hotels.availabilityPage.results.hotelCard.price.pricePerNight",
                {
                  price: nightlyTotalPrice,
                  count: rooms,
                }
              ),
          notDiscounted: nightlyNotDiscountedPrice,
          difference: nightlyPriceDifferenceFormatted,
        },
        totalPrice: t(
          "core-hotels.availabilityPage.results.hotelCard.price.priceForNights",
          {
            price: totalPrice,
            count: totalNights,
          }
        ),
        totalPriceNotDiscounted: getStrikethroughPrice(
          showPointsPricing
            ? (totalNotDiscountedPoints ?? totalNotDiscountedPrice)
            : totalNotDiscountedPrice,
          showPointsPricing ? totalPoints : totalPrice
        ),
        numberOfRooms:
          rooms > 1
            ? t(
                "core-hotels.availabilityPage.results.hotelCard.price.numberOfRooms",
                {
                  count: rooms,
                }
              )
            : null,
        taxesAndFeesIncluded: t(
          "core-hotels.availabilityPage.results.hotelCard.price.taxesAndFeesIncluded"
        ),
      },
    };
  }, [hotel, rewardsAccountId, getPriceDisplay, totalNights, t, rooms]);
};
